import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLogin } from '@/presentation/hooks/useLogin';
import { useToast } from '@/presentation/contexts/ToastContext';
import { Input, Button } from '@/presentation/components/common';
import type { LoginCommand } from '@/application/commands';
import { httpClient } from '@/infrastructure/http/client/httpClient';

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isLoading, error } = useLogin();
  const { success } = useToast();
  const showDevLoginHelpers = import.meta.env.DEV;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [requiresTwoFactorSetup, setRequiresTwoFactorSetup] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [twoFactorSecret, setTwoFactorSecret] = useState<string | null>(null);
  const [savedCredentials, setSavedCredentials] = useState<{ username: string; password: string } | null>(null);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotValue, setForgotValue] = useState('');
  const [isSendingForgot, setIsSendingForgot] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);

  // Funciones helper para rellenar credenciales de prueba
  const fillAdminCredentials = () => {
    setUsername('admin');
    setPassword('TESTINGtesting');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const command: LoginCommand = {
        username,
        password,
        twoFactorCode: requiresTwoFactor ? twoFactorCode : undefined,
        twoFactorSecret: requiresTwoFactorSetup ? twoFactorSecret || undefined : undefined,
      };

      const result = await login(command);

      if (result.requiresTwoFactorSetup) {
        setRequiresTwoFactorSetup(true);
        setQrCodeUrl(result.twoFactorQrCodeUrl || null);
        setTwoFactorSecret(result.twoFactorSecret || null);
        setSavedCredentials({ username, password });
        return;
      }

      if (result.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setSavedCredentials({ username, password });
        setTwoFactorCode(''); // Limpiar código anterior
        setTwoFactorError(null); // Limpiar errores
        return;
      }

      if (result.token && result.user) {
        success('¡Bienvenido!');
        // Redirigir después de 200ms como especifica la documentación
        setTimeout(() => {
          navigate('/dashboard');
        }, 200);
      }
    } catch (err) {
      // Error ya está manejado en el hook
      console.error('Login error:', err);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!savedCredentials) return;

    setTwoFactorError(null);

    // Limpiar y validar código 2FA
    const cleanCode = twoFactorCode.trim().replace(/\s/g, '');
    if (cleanCode.length !== 6 || !/^\d{6}$/.test(cleanCode)) {
      setTwoFactorError('El código debe tener exactamente 6 dígitos numéricos');
      return;
    }

    try {
      console.log('Sending 2FA login:', {
        username: savedCredentials.username,
        hasPassword: !!savedCredentials.password,
        codeLength: cleanCode.length,
        code: cleanCode,
      });

      const command: LoginCommand = {
        username: savedCredentials.username,
        password: savedCredentials.password,
        twoFactorCode: cleanCode,
      };

      const result = await login(command);

      if (result.token && result.user) {
        navigate('/dashboard');
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'Error al verificar el código 2FA';
      setTwoFactorError(errorMessage);
      console.error('2FA verification error:', err);
    }
  };

  const handle2FASetupVerify = async (code: string) => {
    if (!savedCredentials || !twoFactorSecret) return;

    try {
      const command: LoginCommand = {
        username: savedCredentials.username,
        password: savedCredentials.password,
        twoFactorCode: code,
        twoFactorSecret,
      };

      const result = await login(command);

      if (result.token && result.user) {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('2FA setup verification error:', err);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = forgotValue.trim();
    if (!value) {
      setForgotError(t('auth.forgotPasswordRequired'));
      return;
    }

    setIsSendingForgot(true);
    setForgotError(null);
    try {
      await httpClient.post('/auth/forgot-password', { username: value });
      success(t('auth.forgotPasswordSent'));
      setShowForgotPassword(false);
      setForgotValue('');
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setForgotError(message || t('auth.forgotPasswordError'));
    } finally {
      setIsSendingForgot(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-gray-950">
        <div className="max-w-md w-full space-y-8 p-8 bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              {t('auth.recoverPassword')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              {t('auth.forgotPasswordHint')}
            </p>
          </div>
          <form onSubmit={handleForgotPassword} className="mt-8 space-y-6">
            <Input
              label={t('auth.usernameOrEmail')}
              type="text"
              value={forgotValue}
              onChange={(e) => {
                setForgotValue(e.target.value);
                setForgotError(null);
              }}
              required
              autoFocus
            />
            {forgotError ? (
              <div className="text-red-600 dark:text-red-400 text-sm">{forgotError}</div>
            ) : null}
            <Button type="submit" variant="primary" size="lg" className="w-full" isLoading={isSendingForgot}>
              {t('auth.sendTemporaryPassword')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => {
                setShowForgotPassword(false);
                setForgotError(null);
              }}
            >
              {t('auth.backToLogin')}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (requiresTwoFactorSetup && qrCodeUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-gray-950">
        <div className="max-w-md w-full space-y-8 p-8 bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              {t('auth.setup2FA')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              {t('auth.scanQRCode')}
            </p>
          </div>
          <div className="flex justify-center">
            <img src={qrCodeUrl} alt="QR Code" className="w-64 h-64" />
          </div>
          <form onSubmit={(e) => {
            e.preventDefault();
            const code = (e.currentTarget.elements.namedItem('code') as HTMLInputElement)?.value;
            if (code) handle2FASetupVerify(code);
          }} className="mt-8 space-y-6">
            <Input
              label={t('auth.twoFactorCode')}
              type="text"
              name="code"
              required
              placeholder="000000"
              maxLength={6}
            />
            <Button type="submit" variant="primary" size="lg" className="w-full">
              {t('auth.verify')}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (requiresTwoFactor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-gray-950">
        <div className="max-w-md w-full space-y-8 p-8 bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              {t('auth.twoFactorAuth')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              {t('auth.enter2FACode')}
            </p>
          </div>
          <form onSubmit={handle2FASubmit} className="mt-8 space-y-6">
            <div>
              <Input
                label={t('auth.twoFactorCode')}
                type="text"
                value={twoFactorCode}
                onChange={(e) => {
                  // Solo permitir números y limitar a 6 dígitos
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setTwoFactorCode(value);
                  setTwoFactorError(null); // Limpiar error al escribir
                }}
                required
                placeholder="000000"
                maxLength={6}
                pattern="[0-9]{6}"
                inputMode="numeric"
                error={twoFactorError || undefined}
              />
              {showDevLoginHelpers && !twoFactorError && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Ingrese cualquier código de 6 dígitos (ej: 123456)
                </p>
              )}
            </div>
            {(error || twoFactorError) && (
              <div className="text-red-600 dark:text-red-400 text-sm">
                {twoFactorError || error?.message}
              </div>
            )}
            <Button type="submit" variant="primary" size="lg" className="w-full" isLoading={isLoading}>
              {t('auth.verify')}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-gray-950">
      <div className="max-w-md w-full space-y-8 p-8 bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
            {t('auth.login')}
          </h2>
          {showDevLoginHelpers && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={fillAdminCredentials}
              className="mt-6"
              title="Rellenar credenciales de prueba"
            >
              🔧 Rellenar
            </Button>
          )}
        </div>
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <Input
            label={t('auth.username')}
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoFocus
            placeholder={t('auth.username')}
          />
          <Input
            label={t('auth.password')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error && (
            <div className="text-red-600 dark:text-red-400 text-sm">
              {error.message}
            </div>
          )}
          <button
            type="button"
            className="text-sm text-teal-700 hover:underline dark:text-teal-300"
            onClick={() => {
              setForgotValue(username);
              setForgotError(null);
              setShowForgotPassword(true);
            }}
          >
            {t('auth.forgotPassword')}
          </button>
          <Button type="submit" variant="primary" size="lg" className="w-full" isLoading={isLoading}>
            {t('auth.login')}
          </Button>
          
          {showDevLoginHelpers && (
            <div className="mt-4 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-md border border-teal-200 dark:border-teal-800">
              <p className="text-xs text-teal-700 dark:text-teal-300 font-semibold mb-2">
                🔧 Credenciales de prueba (desarrollo):
              </p>
              <div className="text-xs text-teal-600 dark:text-teal-400 mb-2">
                <p><strong>{t('auth.username')}:</strong> admin</p>
                <p><strong>{t('auth.password')}:</strong> TESTINGtesting</p>
                <p className="mt-1 text-gray-500 dark:text-gray-400">
                  Nota: requiere código 2FA (cualquier código de 6 dígitos)
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={fillAdminCredentials}
                className="w-full text-xs"
              >
                Rellenar credenciales
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
